/**
 * Right-rail properties editor, laid out like the KC2 reference:
 *   Properties  → State · Type · Category · Sub Category · Impact ·
 *                 Priority · Complexity · Urgency  (editable, auto-save)
 *   Assignment  → Assignee · Queue · Team          (editable, auto-save)
 *   Organization→ Location · Branch · Department · Campaign · Agent (read-only names)
 *   Tags        → free-text chips
 *   Custom fields
 *
 * Inline auto-save on every change via `onPatch` (PATCH /conversations/:id).
 * Fields without an option source or PATCH support render read-only.
 */
import { useEffect, useMemo, useState, type JSX } from 'react'
import { Flag, Layers, Tag, X } from 'lucide-react'
import { usePriorities } from '../../../hooks/usePriorities.js'
import { useCategories } from '../../../hooks/useCategories.js'
import { useSubcategories } from '../../../hooks/useSubcategories.js'
import { useTypes } from '../../../hooks/useTypes.js'
import { useImpacts } from '../../../hooks/useImpacts.js'
import { useUrgencies } from '../../../hooks/useUrgencies.js'
import { useComplexities } from '../../../hooks/useComplexities.js'
import { useStates } from '../../../hooks/useStates.js'

import { useOrgDetails } from '../../../hooks/useOrgDetails.js'
import { formatDateTime } from '../../../utils/timezone.js'
import type { ConversationDetailResponse } from '../../../modules/conversations/types.js'
import { Field, inputClass, type Option, R, Section, SelectField } from './primitives.js'

type Conversation = ConversationDetailResponse['conversation']

export interface PropertiesPanelProps {
  conversation: Conversation
  onPatch: (patch: Record<string, unknown>, message?: string) => Promise<boolean>
  /** Editing is only allowed for the agent the conversation is assigned to. */
  canEdit: boolean
  onSelfAssign: () => void
  ratingScale: number
}

function ReadOnlyRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="max-w-[60%] truncate text-right font-medium text-slate-800">{value}</span>
    </div>
  )
}

const opts = (arr: { id: string; name: string }[]): Option[] =>
  arr.map((x) => ({ value: x.id, label: x.name }))

export default function PropertiesPanel({
  conversation,
  onPatch,
  canEdit,
  onSelfAssign,
  ratingScale,
}: PropertiesPanelProps): JSX.Element {
  const disabled = !canEdit
  const { states } = useStates(true)
  const { priorities } = usePriorities(true)
  const { categories } = useCategories(true)
  const { subcategories } = useSubcategories(conversation.categoryId ?? undefined)
  const { types } = useTypes(true)
  const { impacts } = useImpacts(true)
  const { urgencies } = useUrgencies(true)
  const { complexities } = useComplexities(true)
  const { orgDetails, fetchOrgDetails } = useOrgDetails()
  useEffect(() => { void fetchOrgDetails() }, [fetchOrgDetails])

  const org: { label: string; value: string | null }[] = [
    { label: 'Location', value: conversation.locationName },
    { label: 'Branch', value: conversation.branchName },
    { label: 'Department', value: conversation.departmentName },
    { label: 'Campaign', value: conversation.campaignName },
    { label: 'Team', value: conversation.assignedTeamName },
    { label: 'Agent', value: conversation.assignedAgentName },
    { label: 'Queue', value: conversation.assignedQueueName },
  ]
  const orgRows = org.filter((r) => r.value)

  /* ── Tags (free-text) ── */
  const [tags, setTags] = useState<string[]>(conversation.tags ?? [])
  const [tagInput, setTagInput] = useState('')
  useEffect(() => setTags(conversation.tags ?? []), [conversation.tags])

  const commitTags = (next: string[]): void => {
    if (!canEdit) return
    setTags(next)
    void onPatch({ tags: next }, next.length === 0 ? 'Tags cleared' : `Tags updated (${next.length})`)
  }
  const addTag = (): void => {
    const t = tagInput.trim()
    if (t && !tags.includes(t)) commitTags([...tags, t])
    setTagInput('')
  }

  /**
   * The panel holds the option list, so it can name the chosen value — the
   * toast reads "Priority set to High" rather than a bare "Updated".
   */
  const set = (field: string) => (v: string) => {
    if (!v || !canEdit) return
    const config = fieldConfigs[field]
    const label = config?.options.find((o) => o.value === v)?.label
    const fieldName = config?.label ?? field
    void onPatch({ [field]: v }, label ? `${fieldName} set to ${label}` : `${fieldName} updated`)
  }

  const DEFAULT_PROPERTY_ORDER = ['state', 'type', 'category', 'subcategory', 'impact', 'priority', 'complexity', 'urgency']

  const fieldConfigs: Record<string, {
    label: string
    value: string
    onChange: (v: string) => void
    options: Option[]
    icon?: JSX.Element
    fieldDisabled?: boolean
    placeholder: string
  }> = {
    state: { label: 'State', value: conversation.statusId ?? '', onChange: set('statusId'), options: opts(states), placeholder: 'Set state' },
    type: { label: 'Type', value: conversation.typeId ?? '', onChange: set('typeId'), options: opts(types), placeholder: 'Set type' },
    category: { label: 'Category', value: conversation.categoryId ?? '', onChange: set('categoryId'), options: opts(categories), icon: <Layers className="h-4 w-4" />, placeholder: 'Set category' },
    subcategory: { label: 'Sub Category', value: conversation.subcategoryId ?? '', onChange: set('subcategoryId'), options: opts(subcategories), fieldDisabled: disabled || !conversation.categoryId, placeholder: conversation.categoryId ? 'Set sub category' : 'Select a category first' },
    impact: { label: 'Impact', value: conversation.impactId ?? '', onChange: set('impactId'), options: opts(impacts), placeholder: 'Set impact' },
    priority: { label: 'Priority', value: conversation.priorityId ?? '', onChange: set('priorityId'), options: opts(priorities), icon: <Flag className="h-4 w-4" />, placeholder: 'Set priority' },
    complexity: { label: 'Complexity', value: conversation.complexityId ?? '', onChange: set('complexityId'), options: opts(complexities), placeholder: 'Set complexity' },
    urgency: { label: 'Urgency', value: conversation.urgencyId ?? '', onChange: set('urgencyId'), options: opts(urgencies), placeholder: 'Set urgency' },
  }

  const orderedPropertyKeys = useMemo(() => {
    const enabledProps = orgDetails?.properties ?? null
    const order = orgDetails?.propertiesOrder ?? null

    if (!enabledProps || enabledProps.length === 0) {
      return DEFAULT_PROPERTY_ORDER
    }

    const hasAll = enabledProps.includes('all')
    const enabledSet = new Set(hasAll ? DEFAULT_PROPERTY_ORDER : enabledProps.filter((p) => p !== 'all'))

    if (order && order.length > 0) {
      const ordered = order.filter((key) => enabledSet.has(key))
      for (const key of DEFAULT_PROPERTY_ORDER) {
        if (enabledSet.has(key) && !ordered.includes(key)) {
          ordered.push(key)
        }
      }
      return ordered
    }

    return DEFAULT_PROPERTY_ORDER.filter((key) => enabledSet.has(key))
  }, [orgDetails?.properties, orgDetails?.propertiesOrder])

  return (
    <div className="space-y-3">
      {/* Assignment gate — properties are editable only by the assigned agent */}
      {disabled && (
        <div className={`${R} border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs`}>
          <div className="mb-1 font-semibold text-amber-800">Properties are read-only</div>
          <p className="mb-2 text-amber-700">
            {conversation.assignedAgentName
              ? `Assigned to ${conversation.assignedAgentName}.`
              : 'This conversation is unassigned.'}{' '}
            Assign it to yourself to edit.
          </p>
          <button
            type="button"
            onClick={onSelfAssign}
            className={`${R} bg-[#3366FF] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#2952CC]`}
          >
            Self assign
          </button>
        </div>
      )}

      {/* Properties (classification) — ordered by org details propertiesOrder */}
      <Section title="Properties">
        <div className="space-y-3">
          {orderedPropertyKeys.map((key) => {
            const config = fieldConfigs[key]
            if (!config) return null
            return (
              <Field key={key} label={config.label}>
                <SelectField
                  value={config.value}
                  onChange={config.onChange}
                  disabled={config.fieldDisabled ?? disabled}
                  placeholder={config.placeholder}
                  options={config.options}
                  icon={config.icon}
                />
              </Field>
            )
          })}
        </div>
      </Section>

      {/* Organization (read-only) */}
      {orgRows.length > 0 && (
        <Section title="Organization">
          <div className="divide-y divide-slate-50">
            {orgRows.map((r) => (
              <ReadOnlyRow key={r.label} label={r.label} value={r.value as string} />
            ))}
          </div>
        </Section>
      )}

      {/* Tags */}
      <Section title="Tags">
        <div className="relative">
          <Tag className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={tagInput}
            disabled={disabled}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addTag()
              }
            }}
            placeholder={disabled ? 'Assign to yourself to edit tags' : 'Add tag and press Enter'}
            className={`${inputClass} pl-9`}
          />
        </div>
        {tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {tags.map((t) => (
              <span
                key={t}
                className={`inline-flex items-center gap-1 ${R} bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600`}
              >
                {t}
                <button
                  type="button"
                  onClick={() => commitTags(tags.filter((x) => x !== t))}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </Section>

      {/* Details (read-only) */}
      <Section title="Details">
        <div className="divide-y divide-slate-50">
          <ReadOnlyRow label="Reference" value={conversation.referenceId} />
          <ReadOnlyRow label="Opened" value={formatDateTime(conversation.openedAt) || '—'} />
          <ReadOnlyRow label="Last message" value={formatDateTime(conversation.lastMessageAt) || '—'} />
          {conversation.transferCount > 0 && (
            <ReadOnlyRow label="Transfers" value={String(conversation.transferCount)} />
          )}
          {conversation.csatScore !== null && (
            <ReadOnlyRow label="CSAT" value={`${conversation.csatScore}/${ratingScale}`} />
          )}
          {conversation.coAgentIds.length > 0 && (
            <ReadOnlyRow label="Collaborators" value={String(conversation.coAgentIds.length)} />
          )}
        </div>
      </Section>
    </div>
  )
}
